import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  Link,
  Container,
  AppBar,
  Toolbar,
  Divider,
  Tooltip,
  Alert,
} from "@mui/material";
import AspectAwareContainer from "../../../components/AspectAwareContainer";
import { VideoDescription } from "../../../components/Video.item";
import EmailIcon from "@mui/icons-material/Email";
import VideoService from "../../../services/video.service";
import { VideoDetails, VideoTimestamp } from "../../../types";
import {
  intToHMS,
  padStartTime,
  dateRepresent,
  handleVideoDownload,
} from "../../../helpers";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { SnackbarProvider } from "notistack";
import { buttonStyle, shareId, notFound } from "~/constants";
import { FileDownload } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import VideoDownloadButton from "./VideoDownload";


export const VideoShare = () => {
  const ref = useRef(null);
  const [video, setVideo] = useState<VideoDetails | null>(null);
  const [tempLinkError, setTempLinkError] = useState<string>("");
  const [download, setDownload] = useState<boolean>(false);
  const linkHasExpired = "This link has already expired";

  const handleTimestampItemClicked = (item: VideoTimestamp) => {
    if (ref?.current?.handleTimeStapmJump) {
      ref.current.handleTimeStapmJump(item.starts_at);
    }
  };

  useEffect(() => {
    if (!shareId) return;

    VideoService.fetchOneByShareId(shareId)
      .then(({ data }) => {
        if (!data) return;

        if (data.video) {
          setVideo(data.video);
          setTempLinkError("");
          return;
        }
        if (!data.video && typeof data?.active === "object") {
          enqueueSnackbar(notFound, { variant: "error" });
          setTempLinkError(notFound);
          return;
        }
        if (!data.video && !data.active) {
          enqueueSnackbar(linkHasExpired, { variant: "error" });
          setTempLinkError(linkHasExpired);
          return;
        }
      })
      .catch((error) => {
        enqueueSnackbar(error.response.data.message, {
          variant: "error",
        });
      });
  }, [shareId]);

  return (
    <>
      <AppBar>
        <Toolbar>
          <img src="/vista_logo_white.png" alt="logo" height="32px" />
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Container sx={{ pr: 0, pl: 0 }}>
        {!video && tempLinkError && (
          <Alert severity="error" sx={{ mt: 4 }}>
            {tempLinkError}
          </Alert>
        )}
        {video && (
          <Box sx={{ mt: { xs: 0, sm: 3 } }}>
            <AspectAwareContainer autoplay video={video} ref={ref} />
            <Grid container component={"div"} sx={{ p: { xs: 2, sm: 0 } }}>
              <Grid size={12}>
                <Stack direction={"column"} spacing={0}>
                  {/* Video details */}
                  <Stack direction={"column"} spacing={1}>
                    <Box sx={{ pt: 2 }}>
                      <VideoDescription big video={video} />
                    </Box>
                    <Grid container sx={{
                      justifyContent: "space-between"
                    }}>
                      <Grid
                        sx={{
                          display: "flex",
                          justifyContent: { sm: "flex-start" },
                        }}
                        size={{
                          xs: 12,
                          sm: "auto"
                        }}>
                        <Stack direction="row" spacing={2} sx={{
                          flexWrap: "wrap"
                        }}>
                          <Tooltip placement="top" title="Quality">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center"
                              }}>
                              <CropOriginalIcon />
                              <Typography sx={{ ml: 1 }}>
                                {video?.metadata?.width}x
                                {video?.metadata?.height}
                              </Typography>
                            </Box>
                          </Tooltip>

                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ mx: 2 }}
                          />

                          <Tooltip placement="top" title="Upload Date">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center"
                              }}>
                              <ScheduleIcon />
                              <Typography sx={{ ml: 1 }}>
                                {dateRepresent(video.created_at).date}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Stack>
                      </Grid>

                      <Grid
                        sx={{
                          display: "flex",
                          justifyContent: { sm: "flex-end" },
                          mt: { xs: 2, sm: 0 },
                        }}
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        {/* Download button */}
                        {video.downloadable && (
                          <VideoDownloadButton
                            url={`/shares/${shareId}/download`}
                          />
                        )}
                        {/* Send feedback button */}
                        <Button
                          disableElevation
                          color="inherit"
                          variant="outlined"
                          sx={{ ...buttonStyle, ml: 1 }}
                          startIcon={<EmailIcon />}
                          href={`mailto:mzuda@novomatic.com?subject=Feedback for video ${video.title}&body=Hello, I would like to give you feedback about the video: ${location.href}`}
                        >
                          Send Feedback
                        </Button>
                      </Grid>
                    </Grid>

                    <Box>
                      {/* Description */}
                      {video?.description ? (
                        <Box>
                          <Stack>
                            <Box>
                              <Typography variant="body1">
                                Description:
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction={"row"} sx={{ mt: 1 }}>
                            <Typography variant="body1">
                              {video?.description}
                            </Typography>
                          </Stack>
                        </Box>
                      ) : (
                        ""
                      )}
                    </Box>
                    {/* Timestamps */}
                    {(video?.timestamps?.length && (
                      <Stack>
                        <Stack direction={"row"}>
                          <Typography variant="body1">Timestamps:</Typography>
                        </Stack>
                        {video.timestamps.map((timestamp, i) => {
                          const values = timestamp.starts_at.split(".");
                          const { hours, minutes, seconds } = intToHMS(
                            parseInt(values[0])
                          );
                          return (
                            <Stack
                              key={i}
                              direction={"row"}
                              sx={{
                                alignItems: "center",
                                justifyContent: "start",
                                mt: 0.2
                              }}>
                              <Box>
                                <Link
                                  onClick={() =>
                                    handleTimestampItemClicked(timestamp)
                                  }
                                  sx={{ cursor: "pointer" }}
                                >
                                  <Typography variant="body2">
                                    {padStartTime(hours)}:
                                    {padStartTime(minutes)}:
                                    {padStartTime(seconds)}
                                  </Typography>
                                </Link>
                              </Box>
                              <Box sx={{ ml: 1 }}>
                                <Typography variant="body2">
                                  {timestamp.text}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )) ||
                      ""}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        )}
      </Container>
    </>
  );
};

export default VideoShare;
